/**
 * Unit conversions for vitals capture.
 *
 * The BE stores values in whatever unit the user entered (along with
 * `value_quantity_unit`), so these are FE-only — primarily used for
 * inline BMI preview, which must compute in kg/m² regardless of whether
 * the practitioner typed lb/in or kg/cm.
 */

export const lbToKg = (lb: number): number => lb * 0.453592;
export const kgToLb = (kg: number): number => kg / 0.453592;
export const inToCm = (inches: number): number => inches * 2.54;
export const cmToIn = (cm: number): number => cm / 2.54;
export const fToC = (f: number): number => (f - 32) * (5 / 9);
export const cToF = (c: number): number => c * (9 / 5) + 32;

/**
 * BMI in kg/m². Accepts mixed units; normalizes internally.
 * Returns null if either input is missing/invalid.
 */
export const computeBmi = (
  weight: number | null | undefined,
  weightUnit: 'lb' | 'kg',
  height: number | null | undefined,
  heightUnit: 'in' | 'cm',
): number | null => {
  if (weight == null || height == null) return null;
  if (!Number.isFinite(weight) || !Number.isFinite(height)) return null;
  if (weight <= 0 || height <= 0) return null;

  const weightKg = weightUnit === 'kg' ? weight : lbToKg(weight);
  const heightCm = heightUnit === 'cm' ? height : inToCm(height);
  const heightM = heightCm / 100;
  if (heightM === 0) return null;

  return Math.round((weightKg / (heightM * heightM)) * 10) / 10;
};
